import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken, getAuthHeader } from '../services/otpAuthService';
import axios from 'axios';
import { ritentaRicollegamentoPush } from './usePushNotifications';

const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '').replace('/api', '')
  : 'http://localhost:5000';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export type NotificationCategory = 'svincolo_atti' | 'voucher' | 'community_feed' | 'community_chat' | 'community_v2' | 'feedback' | 'supporto' | 'sondaggio' | 'rete_certificata' | 'eventi' | 'circolari' | 'tesseramento' | 'sistema';

export interface UnifiedNotification {
  _id: string;
  recipient: string;
  category: NotificationCategory;
  type: string;
  title: string;
  message: string;
  actionUrl: string;
  metadata?: Record<string, any>;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

export interface NotificationCount {
  total: number;
  categories: Partial<Record<NotificationCategory, number>>;
}

interface UseNotificationsOptions {
  enabled?: boolean;
  autoFetch?: boolean;
  limit?: number;
}

export const useNotifications = ({
  enabled = true,
  autoFetch = true,
  limit = 30
}: UseNotificationsOptions = {}) => {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [categoryCount, setCategoryCount] = useState<Partial<Record<NotificationCategory, number>>>({});
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);

  // Fetch notifiche via REST API
  const fetchNotifications = useCallback(async (reset = false) => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      const page = reset ? 1 : pageRef.current;
      const response = await axios.get(`${API_BASE_URL}/notifications`, {
        params: { page, limit },
        headers: getAuthHeader() as any
      });

      if (response.data.success) {
        const { data, pagination } = response.data;
        if (reset) {
          setNotifications(data);
          pageRef.current = 2;
        } else {
          setNotifications(prev => [...prev, ...data]);
          pageRef.current = page + 1;
        }
        setHasMore(pagination.page < pagination.pages);
      }
    } catch (error) {
      console.error('Errore fetch notifiche:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Fetch conteggio non lette
  const fetchCount = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/notifications/count`, {
        headers: getAuthHeader() as any
      });
      if (response.data.success) {
        setUnreadCount(response.data.data.total);
        setCategoryCount(response.data.data.categories);
      }
    } catch (error) {
      console.error('Errore fetch count notifiche:', error);
    }
  }, []);

  // Marca come letta
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/notifications/${notificationId}/read`,
        {},
        { headers: getAuthHeader() as any }
      );
      if (response.data.success) {
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
        );
        // Aggiorna conteggio
        fetchCount();
      }
    } catch (error) {
      console.error('Errore marca notifica letta:', error);
    }
  }, [fetchCount]);

  // Marca tutte come lette
  const markAllAsRead = useCallback(async (category?: NotificationCategory) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/notifications/read-all`,
        category ? { category } : {},
        { headers: getAuthHeader() as any }
      );
      if (response.data.success) {
        setNotifications(prev =>
          prev.map(n => (!category || n.category === category) ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
        );
        fetchCount();
      }
    } catch (error) {
      console.error('Errore marca tutte lette:', error);
    }
  }, [fetchCount]);

  // Elimina notifica
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/notifications/${notificationId}`,
        { headers: getAuthHeader() as any }
      );
      if (response.data.success) {
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
        fetchCount();
      }
    } catch (error) {
      console.error('Errore eliminazione notifica:', error);
    }
  }, [fetchCount]);

  // Marca come lette tutte le notifiche di un voucher specifico
  const markVoucherAsRead = useCallback(async (voucherId: string) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/notifications/mark-read-by-voucher`,
        { voucherId },
        { headers: getAuthHeader() as any }
      );
      if (response.data.success && response.data.modifiedCount > 0) {
        setNotifications(prev =>
          prev.map(n =>
            n.category === 'voucher' && n.metadata?.voucherId === voucherId && !n.isRead
              ? { ...n, isRead: true, readAt: new Date().toISOString() }
              : n
          )
        );
        fetchCount();
      }
    } catch (error) {
      console.error('Errore mark voucher read:', error);
    }
  }, [fetchCount]);


  // Carica più notifiche (pagination)
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(false);
    }
  }, [loading, hasMore, fetchNotifications]);

  // Connessione Socket.IO al namespace /notifications
  useEffect(() => {
    if (!enabled) return;

    const token = getToken();
    if (!token) return;
    // Titolare nella stanza dello studio; dipendente nella sua (il server manda solo le notifiche delle sue sezioni)

    socketRef.current = io(`${SOCKET_URL}/notifications`, {
      auth: { token },
      transports: ['websocket', 'polling']
    });

    let giaConnesso = false;
    socketRef.current.on('connect', () => {
      console.log('🔔 Notification socket connesso');
      setConnected(true);
      // Riconnessione (rete o server tornati): si ritenta il ricollegamento della push web del browser alla
      // persona del token, che un cambio di persona fatto senza rete può aver lasciato a metà. Alla prima
      // connessione ci pensa già l'avvio di usePushNotifications.
      if (giaConnesso) ritentaRicollegamentoPush().catch(() => undefined);
      giaConnesso = true;
    });

    socketRef.current.on('disconnect', () => {
      console.log('🔔 Notification socket disconnesso');
      setConnected(false);
    });

    socketRef.current.on('connect_error', (err: Error) => {
      console.error('Notification socket error:', err.message);
      setConnected(false);
    });

    // Nuova notifica in real-time
    socketRef.current.on('new_notification', (data: any) => {
      // Normalizza id → _id (il backend emette "id", MongoDB usa "_id")
      const notification = { ...data, _id: data._id || data.id };
      setNotifications(prev => [notification, ...prev]);
    });

    // Notifica raggruppata aggiornata (upsert)
    socketRef.current.on('notification_updated', (data: any) => {
      const targetId = data._id || data.id;
      setNotifications(prev => {
        const idx = prev.findIndex(n => n._id === targetId);
        if (idx >= 0) {
          // Aggiorna e sposta in cima
          const updated = { ...prev[idx], ...data, _id: targetId };
          const rest = prev.filter((_, i) => i !== idx);
          return [updated, ...rest];
        }
        return prev;
      });
    });

    // Conteggio aggiornato dal server: `count` è il numero del sito (letta per studio); `totalePersona` è dell'app
    socketRef.current.on('notification_count', ({ count }: { count: number; totalePersona?: number }) => {
      setUnreadCount(count);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [enabled]);

  // Fetch iniziale (anche per il dipendente: il server filtra per le sue sezioni)
  useEffect(() => {
    if (enabled && autoFetch) {
      fetchNotifications(true);
      fetchCount();
    }
  }, [enabled, autoFetch, fetchNotifications, fetchCount]);

  return {
    connected,
    notifications,
    unreadCount,
    categoryCount,
    loading,
    hasMore,
    fetchNotifications: () => fetchNotifications(true),
    fetchCount,
    loadMore,
    markAsRead,
    markAllAsRead,
    markVoucherAsRead,
    deleteNotification
  };
};

export default useNotifications;